import { VERIFIED_COMMUNITY_ONBOARDING_COPY } from '@internal/components/app/constants';
import {
  APPLICATION_STATE_LABELS,
  isEndedApplication,
  isSubscriptionClosed,
} from '@internal/components/projects/helper/application-state';
import {
  ApplicationState,
  ProjectDeclineStage,
} from '@internal/types/projects';

import {
  APPLICATION_ACCEPTED_TEXT,
  CLIENT_REVIEW_PREPARATION_TEXT,
  CLIENT_REVIEW_TEXT,
  CLIENT_REVIEW_TITLE,
  ENDED_OUTCOME_TEXT,
  IN_REVIEW_TEXT,
  OFFER_CANCELLED_TEXT,
  PROJECT_ENDED_TITLE,
  SELECTED_BY_CLIENT_TEXT,
  SUBSCRIPTION_CLOSED_TEXT,
  TALENT_REVIEW_TITLE,
  TEAM_CONFIRMED_FIT_TEXT,
  WITHDRAWN_OUTCOME_TEXT,
} from './application-copy';
import {
  ApplicationTarget,
  BuildInput,
  exploreProjects,
  OutcomeFooterModel,
  RoleProgress,
  ScheduledCallKind,
  SideRailModel,
  StepModel,
  StepStatusTone,
} from './application-model.types';

const outcomeStep = (
  key: string,
  title: string,
  text: string,
  rest: Partial<StepModel> = {}
): StepModel => ({ key, title, text, done: false, active: false, ...rest });

const passedRecruiterReview = (state: ApplicationState): boolean =>
  state === ApplicationState.WithMatching ||
  state === ApplicationState.UnderClientReview ||
  state === ApplicationState.Accepted;

const buildDeclinedOutcome = ({ state, project }: BuildInput): StepModel[] => {
  if (isSubscriptionClosed(state, project)) {
    return [
      outcomeStep(
        'subscription',
        PROJECT_ENDED_TITLE,
        SUBSCRIPTION_CLOSED_TEXT
      ),
    ];
  }
  const feedback = project.declineFeedback?.trim() || ENDED_OUTCOME_TEXT;
  const declinedByClient =
    state !== ApplicationState.Withdrawn &&
    project.declineStage === ProjectDeclineStage.Sales;
  return declinedByClient
    ? [outcomeStep('client', CLIENT_REVIEW_TITLE, feedback)]
    : [outcomeStep('recruiter', TALENT_REVIEW_TITLE, feedback)];
};

const recruiterReviewText = ({
  state,
  project,
  profileReady,
  paused,
  roleProgress: rp,
}: BuildInput): string => {
  if (state === ApplicationState.WithMatching) {
    return CLIENT_REVIEW_PREPARATION_TEXT;
  }
  if (passedRecruiterReview(state)) return TEAM_CONFIRMED_FIT_TEXT;
  if (rp.stopped) {
    return 'Your application is on hold. Our recruiter will review it and come back to you with the next steps.';
  }
  if (paused) return 'Project review is paused.';
  if (state === ApplicationState.InReview && profileReady && rp.verified) {
    return project.isApplicationAccepted
      ? APPLICATION_ACCEPTED_TEXT
      : IN_REVIEW_TEXT;
  }
  return 'We review your role evidence, availability and fit for this project.';
};

const clientReviewText = ({
  state,
  paused,
  roleProgress: rp,
}: BuildInput): string => {
  if (state === ApplicationState.Accepted) return SELECTED_BY_CLIENT_TEXT;
  if (state === ApplicationState.UnderClientReview) return CLIENT_REVIEW_TEXT;
  if (paused || (state === ApplicationState.InReview && rp.stopped)) {
    return 'Client selection is paused.';
  }
  return 'If your application moves forward, your profile will be sent to the client for review.';
};

const buildReviewOutcome = (input: BuildInput): StepModel[] => {
  const { state, roleProgress: rp } = input;
  return [
    outcomeStep('recruiter', TALENT_REVIEW_TITLE, recruiterReviewText(input), {
      done: passedRecruiterReview(state),
      active: state === ApplicationState.InReview && rp.verified,
    }),
    outcomeStep('client', CLIENT_REVIEW_TITLE, clientReviewText(input), {
      done: state === ApplicationState.Accepted,
      status:
        state === ApplicationState.UnderClientReview
          ? { label: 'Pending', tone: StepStatusTone.Muted }
          : undefined,
    }),
  ];
};

export const buildOutcome = (input: BuildInput): StepModel[] => {
  const { state } = input;
  if (state === ApplicationState.NotSelected) {
    return buildDeclinedOutcome(input);
  }
  if (state === ApplicationState.Withdrawn) {
    return [
      outcomeStep('withdrawn', 'Application withdrawn', WITHDRAWN_OUTCOME_TEXT),
    ];
  }
  if (isEndedApplication(state)) {
    return [
      outcomeStep(
        'closed',
        APPLICATION_STATE_LABELS[ApplicationState.ProjectClosed],
        OFFER_CANCELLED_TEXT
      ),
    ];
  }
  return buildReviewOutcome(input);
};

export const buildOutcomeFooter = ({
  state,
  project,
}: BuildInput): OutcomeFooterModel => {
  const ended = isEndedApplication(state);
  return {
    savedNote: ended && Boolean(project.hasCandidateConfirmedApplication),
    explore: ended ? exploreProjects('Explore other projects') : null,
  };
};

const keepGoingText = (rp: RoleProgress, onboarded: boolean): string => {
  if (rp.waiting) {
    return 'Your role checks are awaiting team review. No action needed now.';
  }
  if (rp.scheduledAt) {
    const kind =
      rp.scheduledKind === ScheduledCallKind.Screening
        ? 'recruiter'
        : 'technical';
    return `Your ${kind} interview is scheduled. Your progress is saved for future applications.`;
  }
  return onboarded
    ? 'Finish role verification to start receiving relevant projects.'
    : 'Finish role verification to start receiving relevant projects after onboarding.';
};

const buildKeepGoingRail = (
  rp: RoleProgress,
  onboarded: boolean
): SideRailModel => ({
  title: 'Keep going with your role',
  text: keepGoingText(rp, onboarded),
  action: {
    label:
      rp.waiting || rp.scheduledAt
        ? 'View role progress'
        : 'Continue role verification',
    target: rp.started
      ? ApplicationTarget.Roles
      : ApplicationTarget.RolesExplore,
  },
});

export const buildSideRail = ({
  state,
  paused,
  onboarded,
  hideRoleActions,
  roleProgress: rp,
}: BuildInput): SideRailModel | null => {
  if (!isEndedApplication(state) || hideRoleActions || rp.stopped) return null;
  if (state === ApplicationState.Withdrawn && !rp.verified) return null;

  if (paused) {
    return {
      title: 'Your progress is saved',
      text: 'Applications and verification are paused. You can keep your profile up to date.',
      action: { label: 'Update profile', target: ApplicationTarget.Profile },
    };
  }
  if (!rp.verified) return buildKeepGoingRail(rp, onboarded);
  if (onboarded) {
    return {
      title: 'Your role is ready for other projects',
      text: 'Your verified role is saved. Explore other projects and apply without repeating completed checks.',
      action: exploreProjects('Explore projects'),
    };
  }
  return {
    title: 'Community onboarding',
    text: VERIFIED_COMMUNITY_ONBOARDING_COPY,
    action: { label: 'View progress', target: ApplicationTarget.Community },
  };
};
